import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as y from 'yup';
import {
  createBirthDateSchema,
  createCPFOrCNPJSchema,
  createTestSchema,
} from '../shared/utils/schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSimuladorEnergiaSolarState } from './state';
import { useState } from 'react';
import {
  arePostalCodesEqual,
  validateFullName,
  validatePhone,
  validatePostalCode,
} from '../shared/utils/validation';
import {
  maskTextAndNumbers,
  maskCellPhone,
  maskCNPJ,
  maskCPF,
  maskDate,
  maskEmail,
  maskFullName,
  maskPostalCode,
  unmaskNumber,
} from '../shared/utils/masks';
import { SIMULATION_STEPS } from './simulation-steps';
import { useFindCEP } from '../api/use-find-cep';
import { getErrorData } from '../shared/utils/error';
import { useFindPJOptions } from '../api/use-find-pj-options';
import { useQueue } from '../api/use-queue';
import { useValidateAddress } from '../api/use-validate-address';
import { devLog } from '../../../utils/browser';

const AddressValidations = {
  address: createTestSchema({ required: true }),
  neighborhood: createTestSchema({ required: true }),
  city: createTestSchema({ required: true }),
  state: createTestSchema({ required: true }),
  addressNumber: createTestSchema({
    validate: (v, ctx) => {
      if (ctx.parent.noAddressNumber) {
        return true;
      }

      return !!v && v.trim() !== '';
    },
  }),
};

const AddressValidationsLegalRep = {
  legalRepAddress: createTestSchema({ required: true }),
  legalRepNeighborhood: createTestSchema({ required: true }),
  legalRepCity: createTestSchema({ required: true }),
  legalRepState: createTestSchema({ required: true }),
  legalRepAddressNumber: createTestSchema({
    validate: (v, ctx) => {
      if (ctx.parent.noAddressNumber2) {
        return true;
      }

      return !!v && v.trim() !== '';
    },
  }),
};

const ValidationSettings = {
  'cpf-step-1': {
    document: createCPFOrCNPJSchema('cpf'),
    birthDate: createBirthDateSchema(),
    name: createTestSchema({ required: true, validate: validateFullName }),
    motherName: createTestSchema({ required: true, validate: validateFullName }),
    email: y.string().required('Campo obrigatório').email('E-mail inválido'),
    phone: createTestSchema({
      required: true,
      validate: validatePhone,
    }),
  },
  'cpf-step-2': {
    postalCode: createTestSchema({
      validate: validatePostalCode,
      required: true,
      messages: {
        invalid: 'CEP inválido',
        required: 'CEP é obrigatório',
      },
    }),
  },
  'cnpj-step-1': {
    document: createCPFOrCNPJSchema('cnpj'),
    birthDate: createBirthDateSchema(),
    fantasyName: createTestSchema({ required: true }),
    companyType: createTestSchema({ required: true }),
    phone: createTestSchema({ required: true, validate: validatePhone }),
    socialNature: createTestSchema({ required: true }),
    groupEconomicActivity: createTestSchema({ required: true }),
    economicActivity: createTestSchema({ required: true }),
  },
  'cnpj-step-2': {
    postalCode: createTestSchema({
      validate: validatePostalCode,
      required: true,
      messages: {
        invalid: 'CEP inválido',
        required: 'CEP é obrigatório',
      },
    }),
  },
  'cnpj-step-3': {
    legalRepName: createTestSchema({ required: true, validate: validateFullName }),
    legalRepDocument: createCPFOrCNPJSchema('cpf'),
    legalRepBirthDate: createBirthDateSchema(),
    legalRepPhone: createTestSchema({ required: true, validate: validatePhone }),
    legalRepEmail: y.string().required('Campo obrigatório').email('E-mail inválido'),
    motherName: createTestSchema({ required: true, validate: validateFullName }),
    legalRelationship: createTestSchema({ required: true }),
  },
  'cnpj-step-4': {
    legalRepPostalCode: createTestSchema({
      validate: validatePostalCode,
      required: true,
      messages: {
        invalid: 'CEP inválido',
        required: 'CEP é obrigatório',
      },
    }),
  },
};

const StaticInitialValues = {
  motherName: '',
  nationality: '',
  legalRepName: '',
  legalRepDocument: '',
  legalRepBirthDate: '',
  legalRepPhone: '',
  legalRepEmail: '',
  fantasyName: '',
  companyType: '',
  socialNature: '',
  groupEconomicActivity: '',
  economicActivity: '',
  legalRelationship: '',
  profession: '',
  gender: '',
  legalRepPostalCode: '',
  legalRepAddress: '',
  legalRepAddressNumber: '',
  legalRepComplement: '',
  legalRepNeighborhood: '',
  legalRepCity: '',
  legalRepState: '',
};

export function useFormPersonalData() {
  const [currentStep, setCurrentStep] = useState(1);
  const {
    simulationExclusiveView,
    formLanding,
    formBasic,
    options,
    simulationStep,
    cepData,
    showErrorStep,
    token,
    simulationType,
    EventHandler,
    handleProposalResult,
  } = useSimuladorEnergiaSolarState();

  const apiValidateAddress = useValidateAddress({ token });
  const [legalRepOptions, setLegalRepOptions] = useState([]);

  const formEnabled = simulationStep?.id === SIMULATION_STEPS.FORM_PERSONAL_DATA.id;
  const findCEP = useFindCEP({ token });

  const [postalCodeUpdated, setPostalCodeUpdated] = useState(false);
  const [postalCodeData, setPostalCodeData] = useState(cepData);
  const [legalPostalCodeUpdated, setLegalPostalCodeUpdated] = useState(false);
  const [lealPostalCodeData, setLegalPostalCodeData] = useState(null);
  const [saveProposalSuccess, setSaveProposalSuccess] = useState(false);

  const isCPF = useMemo(() => {
    if (!formLanding?.document) return false;

    return formLanding.document ? formLanding.document.replace(/\D/g, '').length === 11 : false;
  }, [formLanding?.document]);

  const queueFinalStatus = useQueue({
    token,
    key: 'find-final-status',
    timeout: 10000,
    enqueueEndpoint: '/energia-solar-queue/v2/simulation/final-status',
    onError: (error) => {
      showErrorStep({
        ...getErrorData(error),
        event: 'queue-final-status',
        title: 'Erro ao buscar resultado da proposta. Por favor, tente novamente.',
        ctx: 'api-queue-final-status',
      });
    },
    onComplete: handleProposalResult,
  });

  const enqueueSaveProposal = useQueue({
    token,
    key: 'save-proposal',
    timeout: 10000,
    enqueueEndpoint: isCPF
      ? '/energia-solar-queue/v2/simulation/save-proposal-pf'
      : '/energia-solar-queue/v2/simulation/save-proposal-pj',
    onError: (error) => {
      setSaveProposalSuccess(false);
      showErrorStep({
        ...getErrorData(error),
        overridePreviousStep: SIMULATION_STEPS.FORM_PERSONAL_DATA,
        title: 'Erro ao salvar proposta',
        ctx: 'api-queue-save-proposal',
      });
    },
    onComplete: () => {
      setSaveProposalSuccess(true);
      queueFinalStatus.sendToQueue({});
    },
  });

  const maxSteps = useMemo(() => (isCPF ? 2 : 4), [isCPF]);

  const initialValues = useMemo(() => {
    let _documentTypeCpf = '2';

    if (options?.documentTypes) {
      const cpfDocumentType = options.documentTypes.find(
        (type) => String(type.label).toUpperCase().trim() === 'CPF',
      );

      if (cpfDocumentType) {
        _documentTypeCpf = cpfDocumentType.value;
      }
    }

    return {
      ...StaticInitialValues,
      name: formLanding?.name || '',
      document: formLanding?.document || '',
      email: formLanding?.email || '',
      birthDate: formLanding?.birthDate || '',
      phone: formLanding?.phone || '',
      postalCode: formBasic?.postalCode || '',
      address: formBasic?.address || '',
      addressNumber: formBasic?.addressNumber || '',
      complement: formBasic?.complement || '',
      neighborhood: formBasic?.neighborhood || '',
      city: formBasic?.city || '',
      state: formBasic?.state || '',
      documentNumber: formLanding?.document || '',
      documentType: _documentTypeCpf,
      legalRepEmail: formLanding?.email || '',
      noAddressNumber: formBasic?.addressNumber === '' || formBasic?.addressNumber === '0' || false,
      noAddressNumber2: false,
      _documentTypeCpf: _documentTypeCpf,
    };
  }, [formLanding, formBasic, options?.documentTypes]);

  const shouldValidatePostalCode = isCPF ? currentStep === 2 : currentStep === 2;
  const shouldValidateLegalPostalCode = isCPF ? false : currentStep === 4;

  const ValidationSchema = useMemo(() => {
    let shape = {};

    if (isCPF) {
      shape = ValidationSettings[`cpf-step-${currentStep}`];

      if (currentStep === 2 && postalCodeUpdated) {
        shape = { ...shape, ...AddressValidations };
      }
    } else {
      shape = ValidationSettings[`cnpj-step-${currentStep}`];

      if (currentStep === 2 && postalCodeUpdated) {
        shape = { ...shape, ...AddressValidations };
      }

      if (currentStep === 4 && legalPostalCodeUpdated) {
        shape = { ...shape, ...AddressValidationsLegalRep };
      }

      if (currentStep === 3) {
        shape = {
          ...shape,
          legalRelationship: createTestSchema({
            required: true,
            validate: (v) => !!legalRepOptions.find((opt) => opt.value === v),
          }),
        };
      }
    }

    return yupResolver(y.object().shape(shape || {}));
  }, [currentStep, isCPF, legalPostalCodeUpdated, legalRepOptions, postalCodeUpdated]);

  const form = useForm({
    resolver: ValidationSchema,
    values: initialValues,
    shouldUseNativeValidation: false,
    criteriaMode: 'firstError',
    disabled: !simulationExclusiveView || saveProposalSuccess,
  });

  const [
    currentDocumentType,
    currentPostalCode,
    currentLegalRepPostalCode,
    currentNature,
    isNoAddressNumberChecked,
    isNoAddressNumberChecked2,
  ] = form.watch([
    'documentType',
    'postalCode',
    'legalRepPostalCode',
    'socialNature',
    'noAddressNumber',
    'noAddressNumber2',
  ]);

  const { data: pjOptions } = useFindPJOptions({
    token,
    enabled: !isCPF && formEnabled && !!token && currentStep === 3,
    legalCode: currentNature,
  });

  const fields = useMemo(() => {
    let currentFields = {};

    const CPFFields = {
      'step-1': {
        name: {
          label: 'Nome completo',
          maskFunction: maskFullName,
          disabled: !!initialValues.name,
        },
        motherName: { label: 'Nome completo da mãe', maskFunction: maskFullName },
        document: { label: 'CPF', disabled: !!initialValues.document, maskFunction: maskCPF },
        birthDate: { label: 'Data de nascimento', disabled: !!initialValues.birthDate },
        phone: {
          label: 'Telefone',
          maskFunction: maskCellPhone,
          inputMode: 'numeric',
          disabled: !!initialValues.phone,
        },
        email: {
          disabled: !!initialValues.email,
          label: 'E-mail',
          type: 'email',
          maskFunction: maskEmail,
          handleValueChange: (value) => form.setValue('email', value, true),
        },
      },
      'step-2': {
        postalCode: {
          label: 'CEP',
          maskFunction: maskPostalCode,
          inputMode: 'numeric',
          disabled: !!initialValues.postalCode,
        },
        ...(postalCodeUpdated && {
          address: { label: 'Endereço', disabled: !!initialValues.address },
          addressNumber: {
            label: 'Número',
            maskFunction: unmaskNumber,
            inputMode: 'numeric',
            disabled: !!isNoAddressNumberChecked,
          },
          complement: { label: 'Complemento', maskFunction: maskTextAndNumbers },
          neighborhood: { label: 'Bairro', disabled: !!initialValues.neighborhood },
          city: { label: 'Cidade', disabled: true },
          state: { label: 'Estado', disabled: true },
        }),
      },
    };

    const CNPJFields = {
      'step-1': {
        document: { label: 'CNPJ', disabled: !!initialValues.document, maskFunction: maskCNPJ },
        birthDate: {
          label: 'Data de fundação',
          disabled: !!initialValues.birthDate,
          maskFunction: maskDate,
        },
        fantasyName: { label: 'Razão Social', maskFunction: maskTextAndNumbers },
        companyType: {
          label: 'Tipo de empresa',
          type: 'select',
          options: options?.companyTypes || [],
        },
        phone: CPFFields['step-1'].phone,
        socialNature: {
          label: 'Natureza jurídica',
          type: 'select',
          options: options?.legalNatures || [],
        },
        groupEconomicActivity: {
          label: 'Grupo atividade econômica',
          type: 'select',
          options: options?.economicActivityGroups || [],
        },
        economicActivity: {
          label: 'Atividade econômica',
          type: 'select',
          options: options?.economicActivities || [],
        },
      },
      'step-2': CPFFields['step-2'],
      'step-3': {
        legalRepName: { label: 'Nome completo', maskFunction: maskFullName },
        legalRepDocument: { label: 'CPF', maskFunction: maskCPF, inputMode: 'numeric' },
        legalRepBirthDate: {
          label: 'Data de nascimento',
          maskFunction: maskDate,
          inputMode: 'numeric',
        },
        motherName: CPFFields['step-1'].motherName,
        legalRepPhone: {
          label: 'Telefone',
          maskFunction: maskCellPhone,
          inputMode: 'numeric',
        },
        legalRepEmail: {
          label: 'E-mail',
          type: 'email',
          maskFunction: maskEmail,
          handleValueChange: (value) => form.setValue('legalRepEmail', value, true),
        },
        legalRelationship: {
          label: 'Tipo de vínculo com a empresa',
          type: 'select',
          options: pjOptions?.legalRelationships || [],
        },
      },
      'step-4': {
        legalRepPostalCode: { label: 'CEP', maskFunction: maskPostalCode, inputMode: 'numeric' },
        ...(legalPostalCodeUpdated && {
          legalRepAddress: { label: 'Endereço', maskFunction: maskTextAndNumbers },
          legalRepAddressNumber: {
            label: 'Número',
            maskFunction: unmaskNumber,
            inputMode: 'numeric',
            disabled: !!isNoAddressNumberChecked2,
          },
          legalRepComplement: { label: 'Complemento', maskFunction: maskTextAndNumbers },
          legalRepNeighborhood: { label: 'Bairro', maskFunction: maskTextAndNumbers },
          legalRepCity: { label: 'Cidade', disabled: true },
          legalRepState: { label: 'Estado', disabled: true },
        }),
      },
    };

    if (isCPF) {
      currentFields = CPFFields[`step-${currentStep}`];
    } else {
      currentFields = CNPJFields[`step-${currentStep}`];
    }

    if (!currentFields) return [];

    return Object.entries(currentFields).map(([name, value]) => ({ ...value, name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    isCPF,
    options,
    currentDocumentType,
    initialValues._documentTypeCpf,
    postalCodeUpdated,
    legalPostalCodeUpdated,
    pjOptions?.legalRelationships,
    initialValues,
    isNoAddressNumberChecked,
    isNoAddressNumberChecked2,
  ]);

  const gotoPreviousStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const updateCepFieldValues = (values = {}, legalRep = false) => {
    if (legalRep) {
      form.setValue('legalRepAddress', values.address || '', false);
      form.setValue('legalRepNeighborhood', values.neighborhood || '', false);
      form.setValue('legalRepCity', values.city || '', false);
      form.setValue('legalRepState', values.state || '', false);
      return;
    }

    form.setValue('address', values.address || '', false);
    form.setValue('neighborhood', values.neighborhood || '', false);
    form.setValue('city', values.city || '', false);
    form.setValue('state', values.state || '', false);
  };

  /** @type {import('react-hook-form').SubmitHandler<typeof initialValues>} */
  const handleSubmit = async (values) => {
    if (shouldValidatePostalCode && !postalCodeUpdated) {
      if (values.postalCode.length === 9) {
        try {
          const cepResponse = await findCEP.mutateAsync(values.postalCode);
          setPostalCodeData(cepResponse);
          updateCepFieldValues(cepResponse.andress);
        } catch (error) {
          showErrorStep({
            ...getErrorData(error),
            title: 'Erro ao buscar CEP',
            ctx: 'api-find-cep-final',
          });
        }
      }
      return;
    }

    if (shouldValidateLegalPostalCode && !legalPostalCodeUpdated) {
      if (values.legalRepPostalCode.length === 9) {
        try {
          const cepResponse = await findCEP.mutateAsync(values.legalRepPostalCode);
          setLegalPostalCodeData(cepResponse);
          updateCepFieldValues(cepResponse.andress, true);
        } catch (error) {
          showErrorStep({
            ...getErrorData(error),
            title: 'Erro ao buscar CEP',
            ctx: 'api-find-cep-final',
          });
        }
      }
      return;
    }

    if (currentStep !== maxSteps) {
      setCurrentStep((prev) => {
        const next = Math.min(maxSteps, prev + 1);
        return next;
      });
      return;
    }

    const base = {
      nationalityId: options?.nationalities?.[0]?.value || '1',
      professionId: options?.professions?.[0]?.value || '1',
      genderId: options?.genders?.[0]?.value || '1',
    };

    let body = {
      person: {
        name: values.name,
        document: values.document,
        email: values.email,
        birthDate: values.birthDate.split('/').reverse().join('-'),
        phone: values.phone,
        motherName: values.motherName,
        // nationalityId: values.nationality,
        // professionId: values.profession,
        // genderId: values.gender,
        ...base,
      },
      personAddress: {
        postalCode: values.postalCode,
        address: values.address,
        addressNumber: values.addressNumber,
        complement: values.complement,
        neighborhood: values.neighborhood,
        city: values.city,
        cityId: `${postalCodeData?.andress?.cityId || ''}`,
        state: values.state,
        addressType: `${postalCodeData?.andress?.addressType || '1'}`,
      },
    };

    if (!isCPF) {
      body = {
        company: {
          document: values.document,
          openingDate: values.birthDate.split('/').reverse().join('-'),
          phone: values.phone,
          fantasyName: values.fantasyName,
          companyTypeId: values.companyType,
          socialNatureId: values.socialNature,
          groupEconomicActivityId: values.groupEconomicActivity,
          economicActivityId: values.economicActivity,
          email: formLanding.email,
        },
        companyAddress: body.personAddress,
        legalRepresentative: {
          name: values.legalRepName,
          document: values.legalRepDocument,
          email: values.legalRepEmail,
          birthDate: values.legalRepBirthDate.split('/').reverse().join('-'),
          phone: values.legalRepPhone,
          motherName: values.motherName,
          legalRelationshipId: values.legalRelationship,
          // nationalityId: values.nationality,
          // professionId: values.profession,
          // genderId: values.gender,
          ...base,
        },
        legalRepresentativeAddress: {
          postalCode: values.legalRepPostalCode,
          address: values.legalRepAddress,
          addressNumber: values.legalRepAddressNumber,
          complement: values.legalRepComplement,
          neighborhood: values.legalRepNeighborhood,
          city: values.legalRepCity,
          cityId: `${lealPostalCodeData?.andress?.cityId || ''}`,
          state: values.legalRepState,
          addressType: `${lealPostalCodeData?.andress?.addressType || ''}`,
        },
      };
    }

    if (simulationType === 'existing') {
      try {
        const ad = {
          postalCode: values.postalCode,
          address: values.address,
          addressNumber: values.addressNumber,
          complement: values.complement,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          cityId: `${postalCodeData?.andress?.cityId || cepData?.andress?.cityId || '0'}`,
          addressType: `${postalCodeData?.andress?.addressType || cepData?.andress?.addressType || '1'}`,
        };

        await apiValidateAddress.mutateAsync(ad);
      } catch (error) {
        showErrorStep({
          ...getErrorData(error),
          title: 'Erro ao validar endereço',
          ctx: 'api-validate-address-final',
        });
        return;
      }
    }

    try {
      await enqueueSaveProposal.sendToQueue(body);
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        title: 'Erro ao salvar proposta',
        ctx: 'api-queue-save-proposal',
      });
    }

    return;
  };

  useEffect(() => {
    setPostalCodeUpdated(arePostalCodesEqual(currentPostalCode, postalCodeData?.andress?.zipCode));
  }, [postalCodeData?.andress?.zipCode, currentPostalCode]);

  useEffect(() => {
    setLegalPostalCodeUpdated(
      arePostalCodesEqual(currentLegalRepPostalCode, lealPostalCodeData?.andress?.zipCode),
    );
  }, [currentLegalRepPostalCode, lealPostalCodeData?.andress?.zipCode]);

  useEffect(() => {
    if (formEnabled || simulationStep?.id === SIMULATION_STEPS.ERROR_GENERIC.id) return;

    setPostalCodeData(cepData);
  }, [cepData, formEnabled, simulationStep?.id]);

  useEffect(() => {
    setLegalRepOptions(pjOptions?.legalRelationships || []);
  }, [pjOptions?.legalRelationships]);

  useEffect(() => {
    if (!saveProposalSuccess) {
      EventHandler.off('queue-final-status');
      return () => {};
    }

    devLog('Setting up queue final status listener');
    const handler = EventHandler.on('queue-final-status', () => {
      devLog('Received queue final status event: ', saveProposalSuccess);
      queueFinalStatus.sendToQueue({});
    });

    return () => {
      devLog('Cleaning up queue final status listener');
      handler.off('queue-final-status');
    };
  }, [saveProposalSuccess, EventHandler, queueFinalStatus]);

  return {
    form,
    fields,
    handleSubmit: form.handleSubmit(handleSubmit),
    currentStep,
    maxSteps,
    isCPF,
    gotoPreviousStep,
    isNoAddressNumberChecked,
    isNoAddressNumberChecked2,
    shouldValidatePostalCode:
      (shouldValidatePostalCode && !postalCodeUpdated && currentPostalCode.length === 9) ||
      (shouldValidateLegalPostalCode &&
        !legalPostalCodeUpdated &&
        currentLegalRepPostalCode.length === 9),
  };
}
